import React, { type ReactNode } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  View,
  type ViewStyle,
  useWindowDimensions,
} from 'react-native';
import { TabMenuIcon } from './TabBarIcons';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useMenuDrawer } from '../context/MenuDrawerContext';
import { useAppColors } from '../context/PreferencesContext';
import { toCamelCaseLabel } from '../utils/camelCaseLabel';
import { isCompactWidth } from '../utils/responsiveLayout';
import { isWebPlatform, webPointer } from '../utils/webLayout';

type Props = {
  title?: string;
  subtitle?: string | null;
  children?: ReactNode;
  /** When set, shows the Profile-style back control inside the header. */
  onBack?: () => void;
  /** Optional trailing header action (e.g. notifications). Shown left of the menu. */
  right?: ReactNode;
  /**
   * When true (default), shows the menu drawer control on the header's
   * center-right. Set false on the dedicated Menu screen.
   */
  showMenu?: boolean;
  /** When true (default), bleeds into AppScreen horizontal/top padding. */
  bleed?: boolean;
  style?: ViewStyle;
};

export function ScreenHeader({
  title,
  subtitle,
  children,
  onBack,
  right,
  showMenu = true,
  bleed = true,
  style,
}: Props) {
  const palette = useAppColors();
  const { openMenuDrawer } = useMenuDrawer();
  const { width } = useWindowDimensions();
  const compact = isCompactWidth(width);
  /** Web tablet+: a rounded page banner with a left-aligned title. */
  const webWide = isWebPlatform() && !compact;

  const menuControl = showMenu ? (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="Open menu"
      onPress={openMenuDrawer}
      hitSlop={8}
      style={[styles.menuHit, webPointer]}
    >
      <TabMenuIcon color={palette.white} />
    </Pressable>
  ) : null;

  const trailing = (
    <View style={[styles.headerSideEnd, right || menuControl ? styles.headerSideEndAuto : null]}>
      {right}
      {menuControl}
    </View>
  );

  const titleBlock = (
    <View style={[styles.headerCenter, webWide ? styles.headerCenterWeb : null]}>
      {title ? (
        <Text
          accessibilityRole="header"
          numberOfLines={1}
          style={[
            styles.headerTitle,
            webWide ? styles.headerTitleWeb : null,
            { color: palette.white },
          ]}
        >
          {toCamelCaseLabel(title)}
        </Text>
      ) : null}
      {subtitle ? (
        <Text
          numberOfLines={1}
          style={[
            styles.headerSubtitle,
            webWide ? styles.textStartWeb : null,
            { color: palette.white },
          ]}
        >
          {subtitle}
        </Text>
      ) : null}
    </View>
  );

  return (
    <View
      style={[
        styles.hero,
        bleed ? styles.bleed : null,
        bleed && compact ? styles.bleedCompact : styles.fullWidth,
        { backgroundColor: palette.brand },
        isWebPlatform() ? styles.heroWeb : null,
        webWide ? styles.heroWebWide : null,
        webWide && !bleed ? styles.heroWebInset : null,
        style,
      ]}
    >
      {children ? (
        <View style={styles.customWrap}>
          <View style={styles.customBody}>{children}</View>
          {menuControl || right ? (
            <View style={styles.customTrailing}>
              {right}
              {menuControl}
            </View>
          ) : null}
        </View>
      ) : (
        <View style={styles.headerRow}>
          <View
            style={[
              styles.headerSide,
              webWide && !onBack ? styles.headerSideCollapsed : null,
            ]}
          >
            {onBack ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Back"
                onPress={onBack}
                hitSlop={8}
                style={webPointer}
              >
                <Text style={[styles.headerGlyph, { color: palette.white }]}>
                  ‹
                </Text>
              </Pressable>
            ) : null}
          </View>
          {titleBlock}
          {trailing}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  hero: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.lg,
    paddingBottom: spacing.xl,
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    gap: spacing.xs,
    minWidth: 0,
  },
  bleed: {
    marginHorizontal: -spacing.xl,
    marginTop: -spacing.lg,
  },
  /**
   * Mobile: let the negative margins widen the header to the full screen.
   * width: '100%' resolves to the padded content width, which left the header
   * shifted left and short of the right edge.
   */
  bleedCompact: {
    alignSelf: 'stretch',
  },
  fullWidth: {
    width: '100%',
    maxWidth: '100%',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: 0,
    width: '100%',
  },
  headerSide: {
    minWidth: 40,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  headerSideEnd: {
    minWidth: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing.sm,
  },
  headerSideEndAuto: {
    minWidth: 40,
  },
  heroWeb: {
    overflow: 'hidden',
  },
  /**
   * Web tablet+: negative bleed margins plus width: '100%' shifted the header
   * left and short of the right edge, so the banner sits inside the column.
   */
  heroWebWide: {
    marginHorizontal: 0,
    marginTop: 0,
    width: '100%',
    alignSelf: 'stretch',
    borderRadius: 16,
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.lg,
    paddingBottom: spacing.lg,
  },
  /** bleed={false} screens zero the page padding and inset their body by xl. */
  heroWebInset: {
    width: 'auto',
    marginHorizontal: spacing.xl,
    marginTop: spacing.lg,
  },
  headerSideCollapsed: {
    minWidth: 0,
  },
  headerCenterWeb: {
    alignItems: 'flex-start',
    paddingHorizontal: 0,
  },
  headerTitleWeb: {
    fontSize: 22,
    lineHeight: 30,
    textAlign: 'left',
  },
  textStartWeb: {
    textAlign: 'left',
  },
  headerCenter: {
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    gap: 2,
    paddingHorizontal: spacing.xs,
  },
  headerGlyph: {
    fontSize: 32,
    lineHeight: 34,
    fontWeight: '300',
  },
  headerTitle: {
    ...typography.sectionTitle,
    textAlign: 'center',
    fontWeight: '700',
  },
  headerSubtitle: {
    ...typography.caption,
    textAlign: 'center',
    opacity: 0.9,
  },
  menuHit: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  customWrap: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  customBody: {
    flex: 1,
    minWidth: 0,
  },
  customTrailing: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginLeft: spacing.sm,
    paddingTop: 2,
  },
});
